import WizardController from "./wizard_controller"
import timeZoneCityToCountry from "../constants/timeZoneCityToCountry"

let debounce = require("lodash/debounce");

const countryConfig = {
  'United Kingdom': { currency: 'GBP', locale: 'en-GB', symbol: '£' },
  'Australia': { currency: 'AUD', locale: 'en-AU', symbol: '$' },
  'Canada': { currency: 'CAD', locale: 'en-CA', symbol: '$' },
  'United States': { currency: 'USD', locale: 'en-US', symbol: '$' },
  'default': { currency: 'USD', locale: 'en-US', symbol: '$' }
};
const currencies = [...new Set(Object.values(countryConfig).map(config => config.currency))];

// feature/soft-pledge, Feb 13th 2026: Handle hard and soft pledge types.
const pledgeType = Object.freeze({
  SOFT: 'soft',
  HARD: 'hard',
});

const step = Object.freeze({
  CURRENCY_AND_PORTFOLIO: 0,
  PLEDGE: 1,
  PAYMENT: 2,
  PERSONAL_INFORMATION: 3,
  SUMMARY: 4,
  SUBMIT: 5,
});

const hardPledgeStepSequence = Object.freeze([
  step.CURRENCY_AND_PORTFOLIO,
  step.PLEDGE,
  step.PAYMENT,
  step.PERSONAL_INFORMATION,
  step.SUMMARY,
]);

const softPledgeStepSequence = Object.freeze([
  step.CURRENCY_AND_PORTFOLIO,
  step.PLEDGE,
  step.PERSONAL_INFORMATION,
  step.SUMMARY,
]);

export default class extends WizardController {
  static targets = ["AUDPartnerPortfolios", "CADPartnerPortfolios", "GBPPartnerPortfolios", "USDPartnerPortfolios",
    "amountCents", "amountSummary", "currencySelect", "donationEstimate", "emailAddress",
    "estimatedFutureIncomeCurrency", "firstName", "form", "estimatedFutureIncome", "futurePledgeStart",
    "giftAid", "giftAidFieldset", "giftAidHouseNumber", "giftAidPostcode", "giftAidTitle", "lastName",
    "managedPortfolioHiddenInput", "minimumContributionAmountError",
    "paymentMethodHiddenInput", "paymentMethodTypeInput", "paymentMethodCustomerIdInput", "paymentOptions", "pledgePercentage", "pledgeStartMonth", "pledgeStartYear",
    "portfolioOption", "portfolioSummary", "previous", "progress", "progressBarColumn", "promptToUseBankAccount",
    "startDateSummary", "summary", "trialDonationAmount", "trialDonationAmountCurrency",
    "wrongCurrencyNotice", "connectedPaymentMethod", "tipAmountCurrencySymbol", "tipAmountButton", "tipAmountSliderValue"]
  static values = {
    campaignSlug: String,
    campaignCurrency: String,
    currencyToPaymentProcessorAccountIdMapping: Object,
    portfolioIdToNameMapping: Object,
    stripeApiKey: String,
    minimumContributionAmount: Number,
    afterReturnFromSuccessfulStripeCheckout: Boolean,
    pledgeType: String,
  }

  initialize() {
    super.initialize()
    this.updateDonationEstimate = debounce(this.updateDonationEstimate, 500).bind(this)
    this.csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    // Listen for payment modal completion
    this.element.addEventListener('payment-modal:paymentCompleted', this.handlePaymentCompleted.bind(this));
    
    // Listen for portfolio selection
    this.element.addEventListener('portfolio-selected', this.onPortfolioSelected.bind(this));
    
    // Initialize currency-specific display after a short delay to ensure DOM is ready
    setTimeout(async () => {
      this.setUpAdditionalTargets()
      if (this.afterReturnFromSuccessfulStripeCheckoutValue) {
        // The currency and partner are already set by the Rails form. Re-detecting them from the browser location could change `partner_id` and clear `managed_portfolio_id`, so we only sync `currencyCodeValue` with the selected currency.
        const restoredCurrency = this.selectedCurrencyCode
        if (restoredCurrency) this.currencyCodeValue = restoredCurrency
      } else {
        this.setDefaultCurrencyOptionByUserLocation()
      }
      this.refreshPortfolioOptions()
      await this.refreshPaymentMethodOptions()
      
      // Handle return from successful Stripe Checkout (not needed for Payment Element)
      if (this.afterReturnFromSuccessfulStripeCheckoutValue) {
        this.handleReturnFromPaymentSetup()
      }
    }, 100);
  }

  async next(event) {
    event.preventDefault();
    this.validateFields();

    if (this.valid) {
      const nextIndex = this.getNextStepIndex();
      if (nextIndex === null) {
        // Since the Stripe redirect takes several seconds, disable button to prevent multiple requests being sent
        this.progressTarget.setAttribute("disabled", "");
        this.progressTarget.innerText = 'Loading';
        // Last step - submit the form
        this.submitForm();
      } else {
        this.showStep(nextIndex);
      }
    }
  }

  onPortfolioSelected(event) {
    // The radio-select controller should handle setting the hidden input value
    // We need to update the partner_id field based on the selected portfolio
    this.updatePartnerIdFromSelectedPortfolio();
    this.updatePortfolioSummary();
  }

  handleReturnFromPaymentSetup() {
    // Only Stripe Checkout uses this - Payment Element stays on the same page
    // For Stripe Checkout sessions, move to the personal-information step after returning from payment setup
    this.showStep(step.PERSONAL_INFORMATION);
  }


  updatePartnerIdFromSelectedPortfolio() {
    const selectedPortfolioId = this.element.querySelector('[name="pledge_form[managed_portfolio_id]"]')?.value;
    if (selectedPortfolioId) {
      // Find the selected portfolio element and get its partner ID
      const portfolioElement = this.element.querySelector(`[data-radio-select-value="${selectedPortfolioId}"]`);
      if (portfolioElement && portfolioElement.dataset.partnerId) {
        // Update the partner_id hidden field
        const partnerIdField = this.element.querySelector('[name="pledge_form[partner_id]"]');
        if (partnerIdField) {
          partnerIdField.value = portfolioElement.dataset.partnerId;
        }
      }
    }
  }

  onPaymentMethodButtonClicked(event) {
    event.preventDefault();
    
    
    const button = event.currentTarget;
    const paymentMethodType = button.dataset.paymentMethodType;
    const paymentMethodName = button.dataset.paymentMethodName;
    const paymentMethodDescription = button.dataset.paymentMethodDescription;
    
    
    // Get the current partner ID from the form
    const partnerId = this.element.querySelector('[name="pledge_form[partner_id]"]').value;
    
    // Get the Stripe account ID for the current currency
    const stripeAccountId = this.connectedStripeAccountId;
    
    // Get the current partner currency
    const partnerCurrency = this.getCurrentPartnerCurrency();
    
    
    // Find the payment modal controller and show it
    const modalElement = document.querySelector('[data-controller*="payment-modal"]');
    if (modalElement) {
      const modalController = this.application.getControllerForElementAndIdentifier(modalElement, 'payment-modal');
      if (modalController) {
        modalController.show(paymentMethodType, partnerId, paymentMethodName, paymentMethodDescription, stripeAccountId, partnerCurrency);
      }
    }
  }

  handlePaymentCompleted(event) {
    const { buttonType, paymentMethodId, stripePaymentMethodType } = event.detail;
    
    // Store the payment method information for future use
    // Pass all three parameters: button type, payment method ID, and Stripe type
    this.setPaymentMethodData(buttonType, paymentMethodId, stripePaymentMethodType);
    
    // Mark the payment method step as completed using the button type
    this.markPaymentMethodAsSelected(buttonType);
    
    // Show the connected payment method state
    this.showConnectedPaymentMethodState();
  }

  markPaymentMethodAsSelected(paymentMethodType) {
    // Remove any existing selected state from payment method buttons
    const allButtons = this.element.querySelectorAll('.payment-method-button');
    allButtons.forEach(button => {
      button.classList.remove('is-selected');
      button.style.border = '';
    });
    
    // Mark the selected button
    const selectedButton = this.element.querySelector(`[data-payment-method-type="${paymentMethodType}"]`);
    if (selectedButton) {
      selectedButton.classList.add('is-selected');
      selectedButton.style.border = '2px solid #2675F8';
    }
  }

  hasConnectedPaymentMethod() {
    // Check if we have a payment method ID stored
    const paymentMethodId = document.querySelector('[name="pledge_form[payment_processor_payment_method_id]"]')?.value;
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value;
    
    return !!(paymentMethodId && paymentMethodType);
  }

  showConnectedPaymentMethodState() {
    // Hide the payment method selection interface
    const paymentOptions = this.element.querySelector('[data-oftw-wizard-target="paymentOptions"]');
    if (paymentOptions) {
      paymentOptions.style.display = 'none';
    }
    
    // Show the connected payment method display
    let connectedDisplay = this.element.querySelector('[data-oftw-wizard-target="connectedPaymentMethod"]');
    if (!connectedDisplay) {
      connectedDisplay = this.createConnectedPaymentMethodDisplay();
      paymentOptions.parentNode.insertBefore(connectedDisplay, paymentOptions);
    } else {
      // Update the existing display with the current payment method information
      this.updateConnectedPaymentMethodDisplay(connectedDisplay);
    }
    connectedDisplay.style.display = 'block';
  }

  createConnectedPaymentMethodDisplay() {
    // Get the current payment method type from the form field
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value;
    
    // Find the button that corresponds to this payment method type
    const selectedButton = this.element.querySelector(`[data-payment-method-type="${paymentMethodType}"]`);
    const paymentMethodName = selectedButton?.dataset.paymentMethodName || 'Payment Method';
    
    const display = document.createElement('div');
    display.setAttribute('data-oftw-wizard-target', 'connectedPaymentMethod');
    display.className = 'connected-payment-method-display';
    display.innerHTML = `
      <div class="card portfolio-card" style="border: 2px solid #2675F8; background-color: #f8f9ff;">
        <div class="card-content">
          <div class="media">
            <div class="media-left">
              <figure class="image">
                <div class="payment-method-icon">
                  <i class="fas fa-check-circle" style="color: #2675F8; font-size: 1.5rem;"></i>
                </div>
              </figure>
            </div>
            <div class="media-content">
              <p class="title is-5">${paymentMethodName}</p>
              <p class="subtitle small">Payment method connected successfully</p>
            </div>
            <div class="media-right">
              <button type="button" class="button is-small is-outlined" data-action="click->oftw-wizard#replacePaymentMethod">
                Replace
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    return display;
  }
  // feature/integrate-payment-element | September 10th 2025: Update the connected payment method display when the payment method is changed
  updateConnectedPaymentMethodDisplay(connectedDisplay) {
    // Get the current payment method type from the form field
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value;
    
    // Find the button that corresponds to this payment method type
    const selectedButton = this.element.querySelector(`[data-payment-method-type="${paymentMethodType}"]`);
    const paymentMethodName = selectedButton?.dataset.paymentMethodName || 'Payment Method';
    
    // Update the display with the current payment method name
    const titleElement = connectedDisplay.querySelector('.title.is-5');
    if (titleElement) {
      titleElement.textContent = paymentMethodName;
    }
  }

  replacePaymentMethod(event) {
    event.preventDefault();
    
    // Hide the connected display
    const connectedDisplay = this.element.querySelector('[data-oftw-wizard-target="connectedPaymentMethod"]');
    if (connectedDisplay) {
      connectedDisplay.style.display = 'none';
    }
    
    // Show the payment method selection interface
    const paymentOptions = this.element.querySelector('[data-oftw-wizard-target="paymentOptions"]');
    if (paymentOptions) {
      paymentOptions.style.display = 'block';
    }
    
    // Clear the existing payment method data
    this.clearPaymentMethodData();
  }

  clearPaymentMethodData() {
    // Clear all payment method related fields
    if (this.hasPaymentMethodHiddenInputTarget) {
      this.paymentMethodHiddenInputTarget.value = '';
    }
    if (this.hasPaymentMethodTypeInputTarget) {
      this.paymentMethodTypeInputTarget.value = '';
    }
    
    const paymentMethodIdInput = document.querySelector('[name="pledge_form[payment_processor_payment_method_id]"]');
    if (paymentMethodIdInput) {
      paymentMethodIdInput.value = '';
    }
    
    if (this.hasPaymentMethodCustomerIdInputTarget) {
      this.paymentMethodCustomerIdInputTarget.value = '';
    }
    
    // Clear selected state from buttons
    const allButtons = this.element.querySelectorAll('.payment-method-button');
    allButtons.forEach(button => {
      button.classList.remove('is-selected');
      button.style.border = '';
    });
  }

  // Get the current partner currency from the selected partner
  getCurrentPartnerCurrency() {
    const partnerId = this.element.querySelector('[name="pledge_form[partner_id]"]')?.value;
    if (!partnerId) {
      return null;
    }
    
    // Try to get currency from partner data attributes or form
    const partnerElement = this.element.querySelector(`[data-partner-id="${partnerId}"]`);
    if (partnerElement) {
      const currency = partnerElement.dataset.currency;
      if (currency) {
        return currency;
      }
    }
    
    // Fallback: try to get from currency mapping based on connected account
    const currencyMapping = this.currencyToPaymentProcessorAccountIdMappingValue;
    if (currencyMapping) {
      // Find the currency that corresponds to the connected account ID
      const connectedAccountId = this.connectedStripeAccountId;
      if (connectedAccountId) {
        for (const [currency, accountId] of Object.entries(currencyMapping)) {
          if (accountId === connectedAccountId) {
            return currency;
          }
        }
      }
    }
    
    return null;
  }

  showPaymentMethodSelectionInterface() {
    // Hide the connected display if it exists
    const connectedDisplay = this.element.querySelector('[data-oftw-wizard-target="connectedPaymentMethod"]');
    if (connectedDisplay) {
      connectedDisplay.style.display = 'none';
    }
    
    // Show the payment method selection interface
    const paymentOptions = this.element.querySelector('[data-oftw-wizard-target="paymentOptions"]');
    if (paymentOptions) {
      paymentOptions.style.display = 'block';
    }
  }


  // feature/integrate-payment-element | September 10th 2025: Show the connected payment method when returning from step 4 to step 3, with an option to replace it, and preserve the selection when moving from step 3 to 4.

  setPaymentMethodData(buttonType, paymentMethodId, stripePaymentMethodType = null) {
    // If no Stripe type provided, use the button type (backward compatibility)
    const actualStripeType = stripePaymentMethodType || buttonType;
    
    // Set the payment_method_id field (required for validation) - use button type
    if (this.hasPaymentMethodHiddenInputTarget) {
      this.paymentMethodHiddenInputTarget.value = buttonType;
    } else {
      const fallbackField = document.querySelector('[name="pledge_form[payment_method_id]"]');
      if (fallbackField) {
        fallbackField.value = buttonType;
      }
    }
    
    // Also set the payment_method_id field directly for validation
    const paymentMethodIdField = document.querySelector('[name="pledge_form[payment_method_id]"]');
    if (paymentMethodIdField) {
      paymentMethodIdField.value = buttonType;
    }
    
    // Set the payment_processor_payment_method_id field (actual Stripe payment method ID)
    const stripePaymentMethodIdField = document.querySelector('[name="pledge_form[payment_processor_payment_method_id]"]');
    if (stripePaymentMethodIdField) {
      stripePaymentMethodIdField.value = paymentMethodId;
    }
    
    // Set the payment_processor_payment_method_type field (Stripe payment method type)
    if (this.hasPaymentMethodTypeInputTarget) {
      this.paymentMethodTypeInputTarget.value = actualStripeType;
    }
    
    // Clear the customer ID field (will be set by backend)
    if (this.hasPaymentMethodCustomerIdInputTarget) {
      this.paymentMethodCustomerIdInputTarget.value = '';
    }
  }

  ensurePaymentMethodDataIsSet() {
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value;
    const paymentMethodId = document.querySelector('[name="pledge_form[payment_processor_payment_method_id]"]')?.value;
    
    if (paymentMethodType && paymentMethodId) {
      this.setPaymentMethodData(paymentMethodType, paymentMethodId);
    }
  }

  updateDonationEstimate() {
    // Note that when the pledgePercentage button is clicked, it will take some milliseconds to
    // update the corresponding hidden form field. If you're using a decent debounce duration on this
    // function then race conditions will not occur.
    if (this.estimatedFutureIncome && this.pledgePercentageTarget.value) {
      const formattedAmount = this.formatCurrency(this.monthlyDonationEstimate)
      this.donationEstimateTarget.innerText = `Your donation will be ${formattedAmount}/month`
      this.donationEstimateTarget.classList.remove('is-hidden')
      this.amountSummaryTarget.getElementsByClassName('result')[0].innerText = `${formattedAmount}/month`
      this.summaryTarget.innerText = `I pledge ${this.pledgePercentageTarget.value}% of my income to end preventable child death globally.`
      this.amountCentsTarget.value = this.monthlyDonationEstimate * 100
    }
  }

  updatePledgeStartSummary() {
    if (this.futurePledgeStartTarget.checked && this.pledgeStartMonthTarget.value && this.pledgeStartYearTarget.value) {
      const date = `${this.pledgeStartMonthTarget.value} 15, ${this.pledgeStartYearTarget.value}`
      this.startDateSummaryTarget.getElementsByClassName('result')[0].innerText = date
    }
  }

  updatePortfolioSummary() {
    const selectedPortfolioName = this.managedPortfolioHiddenInputTarget.value ?
      this.portfolioIdToNameMappingValue[this.managedPortfolioHiddenInputTarget.value] : ''

    this.portfolioSummaryTarget.getElementsByClassName('result')[0].innerText = selectedPortfolioName
  }

  // Dynamically named target-like elements, generated per currency
  setUpAdditionalTargets() {
    
    
    // Read option text directly from HTML, in case formatting changes (currently we're including flag emoji).
    this.currencyOptionTargets = {
      ['GBP']: this.findOptionByText(this.currencySelectTarget, ['GBP', '£']),
      ['AUD']: this.findOptionByText(this.currencySelectTarget, ['AUD', 'A$', 'AU$']),
      ['CAD']: this.findOptionByText(this.currencySelectTarget, ['CAD', 'C$', 'CA$', 'Can$']),
      ['USD']: this.findOptionByText(this.currencySelectTarget, ['USD', '$'], ['A$', 'AU$', 'C$', 'CA$', 'Can$']),
    }
    this.partnerPortfoliosTargets = {};
    for (let currency of currencies) {
      const target = this.element.querySelector(`[data-oftw-wizard-target=${currency}PartnerPortfolios]`);
      this.partnerPortfoliosTargets[currency] = target;
    }
    
    this.paymentMethodTargets = {};
    for (let currency of currencies) {
      const target = this.element.querySelector(`[data-oftw-wizard-target=${currency}PartnerPaymentOptions]`);
      this.paymentMethodTargets[currency] = target;
    }
  }

  setDefaultCurrencyOptionByUserLocation() {
    
    
    let userCountry;

    if (Intl) {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const tzArr = userTimeZone.split("/");
      const userCity = tzArr[tzArr.length - 1];
      userCountry = timeZoneCityToCountry[userCity];
    }

    const currencyToSelect = countryConfig[userCountry]?.currency || countryConfig['default'].currency;
    
    // Fix: take-pledge-bug | August 14th 2025 : Ensure we have a valid currency option
    if (this.currencyOptionTargets[currencyToSelect]) {
      this.currencySelectTarget.value = this.currencyOptionTargets[currencyToSelect].value;
      this.currencyCodeValue = currencyToSelect;
    } else {
      // Fix: take-pledge-bug | August 14th 2025: Fallback to USD if the detected currency option is not available 
      const fallbackCurrency = 'USD';
      if (this.currencyOptionTargets[fallbackCurrency]) {
        this.currencySelectTarget.value = this.currencyOptionTargets[fallbackCurrency].value;
        this.currencyCodeValue = fallbackCurrency;
      }
    }
    
    // Reset fields on currency change (this will be called by the currencyCodeValue setter)
    this.resetFieldsOnCurrencyChange();
  }

  // Fix: take-pledge-bug | August 14th 2025: If this.selectedCurrencyCode is undefined the default value is used
  currencySelected() {
    let newValue = countryConfig.default.currency; 
    newValue = this.selectedCurrencyCode || newValue;
    this.currencyCodeValue = newValue
    
    // Set the partner ID based on the selected currency
    const partnerId = this.currencySelectTarget.value;
    
    // Reset fields on currency change (refresh methods are called by currencyCodeValue setter)
    this.resetFieldsOnCurrencyChange()
  }

   // Fix: take-pledge-bug | August 14th 2025: uses currentCurrency || 'USD' as a fallback. 
   // It also checks that partnerPortfoliosTargets[currency] exists before manipulating .classList.
  refreshPortfolioOptions() {
    const currentCurrency = this.currencyCodeValue || 'USD';
    
    
    currencies.forEach((currency) => {
      const isHidden = currency !== currentCurrency;
      if (this.partnerPortfoliosTargets[currency]) {
        this.partnerPortfoliosTargets[currency].classList.toggle('is-hidden', isHidden)
      }
    })
  }

  // feature/integrate-payment-element | September 10th 2025: Check if digital wallets (Apple Pay/Google Pay) are available on this device
  async checkDigitalWalletAvailability() {
    try {
      // Initialize Stripe if not already done
      if (!this.stripe) {
        this.stripe = Stripe(this.stripeApiKeyValue);
      }

      // Create a payment request to check availability
      const paymentRequest = this.stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Test',
          amount: 1000,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      const canMakePayment = await paymentRequest.canMakePayment();
      
      return !!canMakePayment;
    } catch (error) {
      return true;
    }
  }

  async refreshPaymentMethodOptions() {
    const currentCurrency = this.currencyCodeValue || 'USD';
    
    
    // Check digital wallet availability
    const digitalWalletAvailable = await this.checkDigitalWalletAvailability();
    
    currencies.forEach((currency) => {
      const isHidden = currency !== currentCurrency;
      if (this.paymentMethodTargets[currency]) {
        this.paymentMethodTargets[currency].classList.toggle('is-hidden', isHidden);
        
        // If this currency section is visible, check for digital wallet option
        if (!isHidden) {
          this.toggleDigitalWalletOption(digitalWalletAvailable);
        }
      }
    });
  }

  // Toggle visibility of digital wallet payment method option
  toggleDigitalWalletOption(isAvailable) {
    const digitalWalletButtons = document.querySelectorAll('[data-payment-method-type="digital_wallets"]');
    digitalWalletButtons.forEach(button => {
      const cardElement = button.closest('.control');
      if (cardElement) {
        if (isAvailable) {
          cardElement.style.display = 'block';
        } else {
          cardElement.style.display = 'none';
        }
      }
    });
  }

  showStep(index) {
    if (index === step.SUBMIT) {
      this.submitForm()
    } else {
      super.showStep(index)
      this.refreshStepLabel(index)
      this.refreshNavButtons(index)
      this.refreshProgressBar(index)
      if (index === step.PERSONAL_INFORMATION) this.updatePledgeStartSummary()
      // fix/tipping-ui | January 7th 2026: Scroll to top of the form when changing steps
      this.scrollToTop()
      // On payment step: show connected payment method if exists, otherwise show selection UI
      if (index === step.PAYMENT && this.hasConnectedPaymentMethod()) {
        // Ensure all payment method data is properly set
        this.ensurePaymentMethodDataIsSet();
        
        this.showConnectedPaymentMethodState();
      } else if (index === step.PAYMENT) {
        this.showPaymentMethodSelectionInterface();
      }
    }
  }

  submitForm() {
    var form = document.getElementById('pledge-form');
    
    this.validateFields()
    if (this.valid) {
      form.submit();
      return false;
    }
  }

  customValidations() {
    // Close drop-downs to make validation messages visible
    document.querySelectorAll('[data-filterable-dropdown-target="menu"]').forEach((menu) => {
      menu.style.display = "none";
    })
    
    // Only run validations relevant to the current step
    let pledgeStartValid = true;
    let postCodeValid = true;
    let donationAmountValid = true;
    let paymentMethodValid = true;
    
    // Pledge calculation validations
    if (this.index >= step.PLEDGE) {
      pledgeStartValid = this.validatePledgeStartDate();
      donationAmountValid = this.validateDonationAmount();
    }
    
    // Gift Aid validations (soft pledge never collects Gift Aid)
    if (this.index >= step.PLEDGE && !this.isSoftPledge()) {
      postCodeValid = this.validatePostCodeField();
    }

    // Payment method validations
    if (this.index >= step.PAYMENT && !this.isSoftPledge()) {
      paymentMethodValid = this.validatePaymentMethod();
    }
    

    return pledgeStartValid && postCodeValid && donationAmountValid && paymentMethodValid
  }

  getPledgeType() {
    return this.pledgeTypeValue.trim().toLowerCase()
  }

  isSoftPledge() {
    return this.getPledgeType() === pledgeType.SOFT
  }

  getStepOrder() {
    const sequences = {
      [pledgeType.SOFT]: softPledgeStepSequence,
      [pledgeType.HARD]: hardPledgeStepSequence,
    }
    return sequences[this.getPledgeType()] || hardPledgeStepSequence
  }

  refreshStepLabel(index) {
    const order = this.getStepOrder()
    const stepNumber = order.indexOf(index) + 1
    const labelEl = this.stepTargets[index]?.querySelector('p.small')
    if (labelEl) labelEl.textContent = `Step ${stepNumber} of ${order.length}`
  }

  getNextStepIndex() {
    const order = this.getStepOrder()
    const idx = order.indexOf(this.index)
    if (idx >= order.length - 1) return null
    return order[idx + 1]
  }

  getPreviousStepIndex() {
    const order = this.getStepOrder()
    const idx = order.indexOf(this.index)
    if (idx <= 0) return null
    return order[idx - 1]
  }

  validatePaymentMethod() {
    // Check if a payment method has been selected or connected
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value;
    const paymentMethodId = document.querySelector('[name="pledge_form[payment_processor_payment_method_id]"]')?.value;
    
    // Check if we have a connected payment method (either from selection or from previous connection)
    const hasConnectedPaymentMethod = !!(paymentMethodType && paymentMethodId);
    
    const errorElement = this.element.querySelector('[data-oftw-wizard-target="paymentOptions"] .error-badge');
    
    if (!hasConnectedPaymentMethod) {
      // Show error message
      if (errorElement) {
        errorElement.style.display = 'block';
      }
      return false;
    } else {
      // Hide error message
      if (errorElement) {
        errorElement.style.display = 'none';
      }
      return true;
    }
  }


  validatePledgeStartDate() {
    let dateIsValid = true;
    if (!this.isElementHidden(this.futurePledgeStartTarget)) {
      let dateIsPresent = this.pledgeStartMonthTarget.value && this.pledgeStartYearTarget.value
      let dateIsRequired = this.futurePledgeStartTarget.checked
      let dateIsInPast = new Date() > this.getPledgeStartDate()
      if (dateIsRequired && !dateIsPresent) {
        dateIsValid = false
      } else {
        dateIsValid = !(dateIsRequired && dateIsInPast)
      }
      [this.pledgeStartMonthTarget, this.pledgeStartYearTarget].forEach((el) => {
        el.classList.toggle("is-danger", !dateIsValid)
      })
      this.pledgeStartMonthTarget.parentElement.parentElement.classList.toggle("field-with-errors", !dateIsValid)
    }
    return dateIsValid
  }

  // What the donor is charged, for the wallet payment sheet.
  currentAmountCents() {
    return parseInt(this.amountCentsTarget?.value, 10) || 0
  }

  //1. fix/google-serv-batch-bugs | September 25th 2025: Don't show error message if contribution amount equals the monthly donation estimate.
  validateDonationAmount() {
    let donationAmountIsValid = true;
    if (!this.isElementHidden(this.estimatedFutureIncomeTarget) && (this.monthlyDonationEstimate < this.minimumContributionAmountValue)) {
      donationAmountIsValid = false;
      this.minimumContributionAmountErrorTarget.innerText = `The minimum monthly donation is ${this.formatCurrency(this.minimumContributionAmountValue)}`  
    }
    this.donationEstimateTarget.parentElement.classList.toggle("field-with-errors", !donationAmountIsValid)
    return donationAmountIsValid
  }

  validatePostCodeField() {
    if(this.isElementHidden(this.giftAidPostcodeTarget)) {
      return true;
    } else {
      const postcodeRegex = /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? [0-9][A-Za-z]{2}|[Gg][Ii][Rr] 0[Aa]{2})$/
      const postcodeValid = postcodeRegex.test(this.giftAidPostcodeTarget.value)
      this.giftAidPostcodeTarget.classList.toggle("is-danger", !postcodeValid)
      this.giftAidPostcodeTarget.parentElement.classList.toggle("field-with-errors", !postcodeValid)
      return postcodeValid
    }
  }

  // Previous step handler
  previous(event) {
    event.preventDefault();
    // show the connected state instead of the selection interface
    if (this.index === step.PERSONAL_INFORMATION && this.hasConnectedPaymentMethod()) {
      this.showConnectedPaymentMethodState();
    }
    const prevIndex = this.getPreviousStepIndex();
    if (prevIndex !== null) {
      this.showStep(prevIndex);
    }
    return false;
  }

  refreshNavButtons(index) {
    const order = this.getStepOrder();
    const labelIndex = order.indexOf(index);
    const isLastStep = labelIndex === order.length - 1;
    const isSummaryStep = labelIndex === order.length - 2;

    let label = 'Next ➝';
    if (isLastStep) label = 'Submit my pledge';
    else if (isSummaryStep) label = 'Continue to summary';

    this.progressTarget.innerText = label;
    this.previousTarget.classList.toggle("is-hidden", index === step.CURRENCY_AND_PORTFOLIO);
  }

  updatePromptToUseBankAccount() {
    this.promptToUseBankAccountTarget.classList.toggle("is-hidden", !this.bankingOption)
  }

  // To encourage the use of banking payment over cards, make banking selected by default
  selectBankingPaymentMethodByDefault() {
    const bankingOption = this.bankingOption // the referent of 'this' changes for the setTimeout anonymous function
    if (bankingOption) {
      // Select banking option by default, or only option if there is only one. Triggers radio-select controller method 'select'
      setTimeout(() => {
        // (When doing this on pageload) wait for radio-select controller to have initialized
        bankingOption.click();
      }, 2000);
    }
  }

  refreshProgressBar(index) {
    const sequence = this.getStepOrder();
    const progressIndex = sequence.indexOf(index);

    this.progressBarColumnTargets.forEach((el, columnIndex) => {
      const columnStep = hardPledgeStepSequence[columnIndex];

      if (!sequence.includes(columnStep)) return;

      const slotIndex = sequence.indexOf(columnStep);
      el.classList.toggle("blue", slotIndex <= progressIndex);
      el.classList.toggle("grey", slotIndex > progressIndex);
    });
  }

  // fix/tipping-ui | January 7th 2026: Scroll to the top when changing steps
  scrollToTop() {
    const progressBar = document.querySelector('.progress-bar')
    const containers = [document.querySelector('.right-page-container'), document.querySelector('.right-page')]
    
    const performScroll = () => {
      containers.forEach(el => el && (el.scrollTop = 0))
      window.scrollTo(0, 0)
      progressBar?.scrollIntoView({ behavior: 'instant', block: 'start' })
    }
    
    [10, 100, 200, 300].forEach(delay => setTimeout(performScroll, delay))
  }

  formatCurrency(value) {
    const currencyToLocale = {};

    for (let country in countryConfig) {
      const { currency, locale } = countryConfig[country];
      currencyToLocale[currency] = locale;
    }

    const locale = currencyToLocale[this.currencyCodeValue] || 'en-US';  // Default to English - United States

    const minimumFractionDigits = (parseFloat(value) === parseInt(value)) ? 0 : 2

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.currencyCodeValue,
      minimumFractionDigits: minimumFractionDigits,
      maximumFractionDigits: 2
    }).format(value);
  }

  getPledgeStartDate() {
    const monthIndex = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].indexOf(this.pledgeStartMonthTarget.value);

    return new Date(this.pledgeStartYearTarget.value, monthIndex, 1);
  }

  findOptionByText(selectElement, searchStrings, excludeStrings = []) {
    for (const searchString of searchStrings) {
      const foundOption = [...selectElement.options].find(opt => {
        if (excludeStrings.some(exclude => opt.text.includes(exclude))) {
          return false;
        }
        return opt.text.includes(searchString);
      });

      if (foundOption) {
        return foundOption;
      }
    }
    return null;
  }

  updateEstimatedFutureIncomeCurrency() {
    this.estimatedFutureIncomeCurrencyTarget.innerText = this.currencySymbol;
  }

  updateTrialDonationAmountCurrency() {
    this.trialDonationAmountCurrencyTargets.map((target) => {
      target.innerText = this.currencySymbol;
    })
  }

  // fix/tipping-ui | January 7th 2026: Update tip amount currency symbols
  updateTipAmountCurrencySymbol() {
    if (this.hasTipAmountCurrencySymbolTarget) {
      this.tipAmountCurrencySymbolTarget.innerText = this.currencySymbol;
    }
  }

  updateTipAmountButtons() {
    if (this.hasTipAmountButtonTarget) {
      this.tipAmountButtonTargets.forEach((button) => {
        const amount = parseFloat(button.dataset.amount);
        button.innerText = this.formatCurrency(amount);
      });
    }
    if (this.hasTipAmountSliderValueTarget) {
      this.tipAmountSliderValueTarget.innerText = this.formatCurrency(5);
    }
  }

  updateWrongCurrencyNotice() {
    this.wrongCurrencyNoticeTarget.innerText = this.currencySymbol;
  }

  toggleGiftAidFieldset() {
    // Soft pledge never collects Gift Aid; keep hidden even if donor switches currency to GBP.
    if (this.isSoftPledge()) {
      this.giftAidFieldsetTarget.classList.add('is-hidden')
      return
    }
    if (this.currencyCodeValue === 'GBP') {
      this.giftAidFieldsetTarget.classList.remove('is-hidden')
    } else {
      this.giftAidFieldsetTarget.classList.add('is-hidden')
      Array.from(this.giftAidFieldsetTarget.getElementsByTagName('input')).forEach((el) => {el.value = ""})
    }
  }

  resetFieldsOnCurrencyChange() {
    // The below should only happen when the currency value is set by the user directly operating on the select, or when
    // we operate on it using JS (based on the user location or campaign), since these methods deliberately alter other
    // form fields' values.
    // That is: this should not run when the currency value is set by the view_model in Rails.

    this.managedPortfolioHiddenInputTarget.value = null;
    
    // DON'T clear payment method fields if a payment method has already been connected
    // This prevents clearing the fields after the user has set up their payment method
    const hasConnectedPaymentMethod = this.hasConnectedPaymentMethod();
    
    if (!hasConnectedPaymentMethod) {
      this.paymentMethodHiddenInputTarget.value = null;
      this.paymentMethodTypeInputTarget.value = null;
      this.paymentMethodCustomerIdInputTarget.value = null;
      
      // Clear any selected payment method buttons
      const allButtons = this.element.querySelectorAll('.payment-method-button');
      allButtons.forEach(button => {
        button.classList.remove('is-selected');
        button.style.border = '';
      });
    }
  }

  // Return the payment processor's code for the payment method that is a direct debit (i.e. not card, paypal, etc).
  get bankingOption() {
    return ['us_bank_account', 'bacs_debit', 'acss_debit'].map((value) => {
      return this.paymentMethodTargets[this.currencyCodeValue].querySelector(`[data-radio-select-value='${value}']`)
    }).find(el => el !== null)
  }

  get connectedStripeAccountId() {
    // Get the specific partner's account ID from the selected portfolio
    const selectedPortfolioId = this.element.querySelector('[name="pledge_form[managed_portfolio_id]"]')?.value;
    if (selectedPortfolioId) {
      // Find the selected portfolio element and get its partner's account ID
      const portfolioElement = this.element.querySelector(`[data-radio-select-value="${selectedPortfolioId}"]`);
      if (portfolioElement && portfolioElement.dataset.stripeAccountId) {
        return portfolioElement.dataset.stripeAccountId;
      }
    }
    
    // Fallback to currency-based mapping for backward compatibility
    return this.currencyToPaymentProcessorAccountIdMappingValue[this.currencyCodeValue]
  }

  get estimatedFutureIncome() {
    return this.estimatedFutureIncomeTarget.value.match(/\d+/g)?.join('');
  }

  get monthlyDonationEstimate() {
    let estimate = parseFloat(parseFloat(this.estimatedFutureIncome)/12.0) * (parseFloat(this.pledgePercentageTarget.value)/100.0)

    return estimate.toFixed(0)
  }

  get currencySymbol() {
    for (let country in countryConfig) {
      if (countryConfig[country].currency === this.currencyCodeValue) {
        return countryConfig[country].symbol;
      }
    }
  }

  get currencyCodeValue() {
    return this.data.currencyCodeValue;
  }

  get selectedCurrencyCode() {
    for (let currency of currencies) {
      if (this.currencyOptionTargets[currency] && this.currencyOptionTargets[currency].selected) {
        return currency;
      }
    }
  }

  set currencyCodeValue(value) {
    this.data.currencyCodeValue = value;
    this.refreshPortfolioOptions()
    this.refreshPaymentMethodOptions()
    this.updateEstimatedFutureIncomeCurrency()
    this.updateTrialDonationAmountCurrency()
    this.updateTipAmountCurrencySymbol()
    this.updateTipAmountButtons()
    this.updateWrongCurrencyNotice()
    this.updateDonationEstimate()
    this.updatePromptToUseBankAccount()
    this.toggleGiftAidFieldset()
  }
}